/**
 * settlement_names.ts — deterministic, civ-flavored settlement names.
 *
 * The `settlement_name` table only holds ADMIN renames (sparse), so the world
 * map derives a canonical name for every site from its seed: Aurelian Concord
 * settlements read latinate-solar (Solvarum, Corvessa, Thalaris), Borean
 * Compact settlements read nordic-ice (Skjolmark, Fenholt, Brynstad). Names
 * are resolved over the WHOLE site list in siteId order with a deterministic
 * re-roll on collision, so every client derives the identical atlas. An admin
 * rename (settlement_name row) overrides the derived name wherever shown.
 */
import type { SettlementSite } from '@shared/settlements.js';
import type { Civilization } from './biome_settlements.js';
import { CAPITAL_NAMES, CIRCUIT_NAME, districtName } from '@shared/capitals.js';

const A_FIRST = ['Aure', 'Sol', 'Vel', 'Cor', 'Thal', 'Mar', 'Or', 'Lu', 'Ser', 'Cal', 'Ves', 'Alta', 'Ner', 'Ost', 'Pra', 'Vir'];
const A_MID = ['', 'a', 'e', 'i', 'o', 'u'];
const A_LAST = ['rum', 'ris', 'ra', 'va', 'ssa', 'ntis', 'lior', 'dea', 'cine', 'rch', 'lus', 'mea'];

const B_FIRST = ['Skjol', 'Bryn', 'Ulf', 'Is', 'Fen', 'Kald', 'Nor', 'Ei', 'Sten', 'Hav', 'Vind', 'Bjor', 'Frost', 'Jokk', 'Myr', 'Tind'];
const B_MID = ['', 'a', 'e', 'i', 'o'];
const B_LAST = ['den', 'mark', 'wynn', 'holt', 'gard', 'mere', 'stad', 'fell', 'brook', 'shaw', 'ridge', 'vale', 'thorn', 'crest', 'vik', 'ness'];

/** FNV-1a → [0,1) — mirrors the settlement/civ hash family. */
function h01(seed: number, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

function compose(siteSeed: number, civ: Civilization, roll: number): string {
  const [F, M, L] = civ === 'a' ? [A_FIRST, A_MID, A_LAST] : [B_FIRST, B_MID, B_LAST];
  const f = F[Math.floor(h01(siteSeed, `nf${roll}`) * F.length) % F.length];
  const m = M[Math.floor(h01(siteSeed, `nm${roll}`) * M.length) % M.length];
  const l = L[Math.floor(h01(siteSeed, `nl${roll}`) * L.length) % L.length];
  // Avoid awkward vowel doubling at the joins.
  const mid = m !== '' && f.endsWith(m) ? '' : m;
  return f + mid + l;
}

/**
 * Name every site, deterministically unique: sites are processed in siteId
 * order and a collision re-rolls with an incremented salt — same inputs, same
 * atlas, on every client.
 */
export function settlementNamesFor(sites: SettlementSite[], civOf: (siteSeed: number, siteId?: number) => Civilization): Map<number, string> {
  const used = new Set<string>();
  const out = new Map<number, string>();
  for (const s of [...sites].sort((a, b) => a.id - b.id)) {
    const civ = civOf(s.siteSeed, s.id);
    let name: string;
    // Capital wave: fixed capital + circuit names; districts carry their program name.
    if (s.tier === 'capital') name = CAPITAL_NAMES[civ];
    else if (s.tier === 'district') name = districtName(civ, s.district ?? 0);
    else if (s.tier === 'circuit') name = CIRCUIT_NAME;
    else {
      name = compose(s.siteSeed, civ, 0);
      for (let roll = 1; used.has(name) && roll < 40; roll++) name = compose(s.siteSeed, civ, roll);
    }
    used.add(name);
    out.set(s.id, name);
  }
  return out;
}
